import { EstimatedSurcharge, GetPaymentMethodResponse, GetPaymentMethodsResponse, PaymentMethodType, Transaction } from '@ar/network/AccountsReceivable.network';
import BigNumber from 'bignumber.js';
import { getCurrencyFormatFromCents } from '@v2/utils/CurrencyUtils';

export type PaymentMethodByCustomer = Record<string, GetPaymentMethodsResponse>;
export type InvoicesByCustomer = Record<string, Transaction[]>;
export type InvoicesBySupplier = Record<string, Transaction[]>;
export const FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY = 'FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY';

/**
 * Groups invoices by customer ID.
 *
 * @param invoices - An array of transactions representing invoices.
 * @returns An object where the keys are customer IDs and the values are arrays of transactions.
 */
export const groupInvoicesByCustomerID = (invoices: Transaction[] = []): InvoicesByCustomer => invoices?.reduce((acc, inv) => {
    const customerID = inv.customer_id;
    if (!acc[customerID]) {
        acc[customerID] = [];
    }
    acc[customerID].push(inv);
    return acc;
}, {}) ?? {};

/**
 * Groups invoices by supplier ID.
 *
 * @param invoices - An array of transactions representing invoices.
 * @returns An object where the keys are supplier IDs and the values are arrays of transactions.
 */
export const groupInvoicesBySupplierID = (invoices: Transaction[] = []): InvoicesBySupplier => invoices?.reduce((acc, inv) => {
    const supplierID = inv.supplier_id;
    if (!acc[supplierID]) {
        acc[supplierID] = [];
    }
    acc[supplierID].push(inv);
    return acc;
}, {}) ?? {};

/**
 * Checks if all invoices are from the same customer.
 *
 * @param invoices - An array of transactions representing invoices.
 * @returns A boolean indicating whether all invoices are from the same customer.
 */
export const areAllInvoicesFromSameCustomer = (invoices: Transaction[] = []): boolean => {
    if (!invoices?.length) {
        return false;
    }
    const { customer_id: firstCustomerID } = invoices[0];
    return invoices.every((invoice) => invoice.customer_id === firstCustomerID);
};

/**
 * Sums up all surcharges for a combined invoice for a given payment method.
 *
 * @param paymentMethod - A payment method used to charge invoices
 * @param estimatedSurcharges - A 2D array of estimated surcharges for all invoices for each supported payment method type
 */
export const getBulkEstimatedSurcharge = (paymentMethod: GetPaymentMethodResponse, estimatedSurcharges: EstimatedSurcharge[][]): BigNumber => {
    let surchargeAmount = new BigNumber(0);
    if (paymentMethod && estimatedSurcharges?.length) {
        const estimatedSurcharge = estimatedSurcharges.flat().filter((surcharge) => surcharge?.paymentType?.type?.toLowerCase() === paymentMethod.brand?.toLowerCase());
        const estimatedSurchargeAmount = estimatedSurcharge?.reduce((acc, surcharge) => acc + surcharge.estimatedSurcharge, 0);
        surchargeAmount = surchargeAmount.plus(estimatedSurchargeAmount).dividedBy(100);
    }
    return surchargeAmount;
};

/**
 * Calculates surcharge for an amount based on the surcharge rate from estimated surcharges.
 * The surcharge is recalculated proportionally based on the amount after credits are applied.
 *
 * @param paymentMethod - A payment method used to charge invoices
 * @param estimatedSurcharges - A 2D array of estimated surcharges for all invoices
 * @param totalInvoiceAmount - Total invoice amount in dollars before credits
 * @param amountAfterCredits - Amount in dollars after credits are applied
 */
export const calculateSurchargeAfterCredits = (
    paymentMethod: GetPaymentMethodResponse,
    estimatedSurcharges: EstimatedSurcharge[][],
    totalInvoiceAmount: number,
    amountAfterCredits: number
): BigNumber => {
    if (!paymentMethod || !estimatedSurcharges?.length || amountAfterCredits <= 0 || totalInvoiceAmount <= 0) {
        return new BigNumber(0);
    }

    const flatSurcharges = estimatedSurcharges.flat();
    const totalOriginalSurchargeInCents = flatSurcharges
        .filter((surcharge) => surcharge?.paymentType?.type?.toLowerCase() === paymentMethod.brand?.toLowerCase())
        .reduce((acc, surcharge) => acc + (surcharge.estimatedSurcharge || 0), 0);

    if (totalOriginalSurchargeInCents === 0) {
        return new BigNumber(0);
    }

    const surchargeRate = totalOriginalSurchargeInCents / (totalInvoiceAmount * 100);

    const newSurchargeInCents = amountAfterCredits * 100 * surchargeRate;

    return new BigNumber(newSurchargeInCents).dividedBy(100);
};

/**
 * Returns the estimated surcharge for an invoice for a given payment method.
 *
 * @param paymentMethod - A payment method used to charge invoices
 * @param estimatedSurcharges - An array of estimated surcharges for each supported payment method type
 */
export const getEstimatedSurcharge = (paymentMethod: GetPaymentMethodResponse, estimatedSurcharges: EstimatedSurcharge[]): BigNumber => {
    let surchargeAmount = new BigNumber(0);
    if (paymentMethod?.type === PaymentMethodType.CARD && estimatedSurcharges?.length) {
        const estimatedSurcharge = estimatedSurcharges.find((surcharge) => surcharge?.paymentType?.type?.toLowerCase() === paymentMethod.brand?.toLowerCase());
        const estimatedSurchargeAmount = estimatedSurcharge?.estimatedSurcharge;
        surchargeAmount = surchargeAmount.plus(estimatedSurchargeAmount).dividedBy(100);
    }
    return surchargeAmount;
};

/**
 * Calculates surcharge for a single invoice based on the surcharge rate from estimated surcharges.
 * The surcharge is recalculated proportionally based on the amount after credits are applied.
 *
 * @param paymentMethod - A payment method used to charge invoices
 * @param estimatedSurcharges - An array of estimated surcharges for the invoice
 * @param totalInvoiceAmount - Total invoice amount in dollars before credits
 * @param amountAfterCredits - Amount in dollars after credits are applied
 */
export const calculateSingleSurchargeAfterCredits = (
    paymentMethod: GetPaymentMethodResponse,
    estimatedSurcharges: EstimatedSurcharge[],
    totalInvoiceAmount: number,
    amountAfterCredits: number
): BigNumber => {
    if (!paymentMethod || !estimatedSurcharges?.length || amountAfterCredits <= 0 || totalInvoiceAmount <= 0) {
        return new BigNumber(0);
    }

    // Find the original surcharge for the selected payment method
    const originalSurcharge = estimatedSurcharges.find(
        (surcharge) => surcharge?.paymentType?.type?.toLowerCase() === paymentMethod.brand?.toLowerCase()
    );

    if (!originalSurcharge || !originalSurcharge.estimatedSurcharge) {
        return new BigNumber(0);
    }

    const originalSurchargeInCents = originalSurcharge.estimatedSurcharge;

    // Calculate the surcharge rate (surcharge / invoice amount)
    const surchargeRate = originalSurchargeInCents / (totalInvoiceAmount * 100);

    // Apply the rate to the amount after credits
    const newSurchargeInCents = amountAfterCredits * 100 * surchargeRate;

    return new BigNumber(newSurchargeInCents).dividedBy(100);
};

/**
 * Formats estimated surcharges to be displayed.
 *
 * @param surchargeAmount - A BigNumber storing the surcharge amount
 */
export const formatSurchargeAmount = (surchargeAmount: BigNumber): string => {
    const positivePrefix = '+';
    const formattedAsCurrency = getCurrencyFormatFromCents(surchargeAmount);
    return surchargeAmount.isGreaterThan(0) ? (positivePrefix + formattedAsCurrency) : formattedAsCurrency;
};
